import * as d3 from 'd3';
import type { Selection } from 'd3';
import { getPalette, type Color, type ColorPalette } from './color';
import {
  INTERACTION_CLASS,
  barInteraction,
  labelInteraction,
  labelBackgroundInteraction,
  type InteractionStore
} from './interactions';

export const applyStylesToElement = (
  element: HTMLElement,
  styleObject: { [key: string]: string }
) => {
  Object.entries(styleObject).forEach(([key, value]) => {
    (<any>element.style)[key] = value;
  });
};

type GradientStop = {
  color: string;
  length: number | undefined;
};

/**
 * Append an empty SVG with gradient defintions to the body.
 * Because we double render the chart on mobile and desktop and hide one of them,
 * on mobile the charts are unable to access the definitions in the desktop charts.
 */
export const addGradients = (gradients: Color[] = []) => {
  const gradient = d3
    .select(document.body)
    .append('svg')
    .style('height', '0px')
    .style('width', '0px')
    .style('position', 'absolute')
    .style('left', '-1000px')
    .append('defs')
    .selectAll('linearGradient')
    .data(gradients)
    .enter()
    .append('linearGradient')
    .attr('id', function (d) {
      return d.id;
    })
    .attr('gradientTransform', function (d) {
      return `rotate(${d.angle || 0})`;
    });

  gradient
    .selectAll('stops')
    .data(function (d): GradientStop[] {
      if (!d.colors) return [];

      return d.colors?.map((color) => {
        return { color, length: d.colors?.length };
      });
    })
    .enter()
    .append('stop')
    .attr('offset', function (d, index) {
      const length = d.length || 1;
      const percent = (100 / (length - 1)) * index;

      return `${percent}%`;
    })
    .attr('stop-color', function (d) {
      return d.color;
    })
    .attr('stop-opacity', 1);
};

export type AxisLabel = {
  alignmentbaseline?: string;
  labelStyle?: 'primary' | 'secondary';
  textAnchor?: string;
  text: string;
  x: number;
  y: number;
};

const splitAxisLabel = function (label: AxisLabel): AxisLabel[] {
  if (label.text?.length > 20) {
    const words = label.text.split(' ');
    const middleIndex = Math.floor(words.length / 2);
    const margin = 18;

    const firstHalfOfLabel = {
      ...label,
      y: label.y - margin,
      text: words.slice(0, middleIndex).join(' ')
    };
    const secondHalfOfLabel = {
      ...label,
      y: label.y + margin,
      text: words.slice(middleIndex).join(' ')
    };

    return [firstHalfOfLabel, secondHalfOfLabel];
  }

  return [label];
};

export const addAxisLabels = function (
  selection: Selection<SVGGElement, unknown, null, undefined>,
  data: AxisLabel[]
) {
  const fontStyles = {
    // Holding off on updating chart type styles for BLOCK-WEB-BRAND-RESKIN
    // These cannot be updated to Cash Sans until design team has time to review
    primary: {
      family: 'Pilat Condensed',
      size: 24,
      weight: 700,
      lineHeight: '110%'
    },
    secondary: {
      family: 'Pilat Wide',
      size: 16,
      weight: 400,
      lineHeight: '100%'
    }
  };

  selection
    .selectAll('.s')
    .append('div')
    .data(function () {
      return data.map(splitAxisLabel).flat();
    })
    .enter()
    .append('text')
    .text(function (d) {
      return d.text;
    })
    .attr('x', function (d) {
      return d.x;
    })
    .attr('y', function (d) {
      return d.y;
    })
    .attr('fill', '#ffffff')
    .attr('alignment-baseline', function (d) {
      return d.alignmentbaseline || 'middle';
    })
    .attr('text-anchor', function (d) {
      return d.textAnchor || 'middle';
    })
    .style('font-family', function (d) {
      return fontStyles[d.labelStyle || 'primary'].family;
    })
    .style('font-weight', function (d) {
      return fontStyles[d.labelStyle || 'primary'].weight;
    })
    .attr('font-size', function (d) {
      const fontSize = fontStyles[d.labelStyle || 'primary'].size;

      if (d.labelStyle === 'secondary') {
        return `calc(${fontSize}px * var(--scaling-ratio, 1))`;
      }
      return `calc(var(--primary-label-font-size, ${fontSize}) * var(--scaling-ratio, 1))`;
    })
    .attr('line-height', function (d) {
      return fontStyles[d.labelStyle || 'primary'].lineHeight;
    });
};

export type Bar = {
  color: Color | undefined;
  height: number;
  isGradient: boolean | undefined;
  width: number;
  x: number;
  y: number;
};

export const addBarSegements = function (
  selection: Selection<SVGGElement, unknown, null, undefined>,
  data: Bar[],
  store: InteractionStore
) {
  selection
    .selectAll('.bar')
    .data(data)
    .enter()
    .append('rect')
    .attr('x', function (d) {
      return d.x;
    })
    .attr('y', function (d) {
      return d.y;
    })
    .attr('width', function (d) {
      return d.width;
    })
    .attr('height', function (d) {
      return d.height;
    })
    .attr('fill', function (d) {
      return d.isGradient ? `url(#${d?.color?.id})` : d?.color?.color || '';
    })
    .attr('stroke', 'black')
    .attr('stroke-width', '2')
    .each(function (d) {
      const colorId = d.color?.id || '';
      const classname = `${INTERACTION_CLASS} ${barInteraction.classname} ${colorId}`;
      const interactions = [barInteraction, labelInteraction, labelBackgroundInteraction];
      const selected = colorId ? [colorId] : [];

      d3.select(this)
        .style('cursor', 'pointer')
        .attr('class', classname)
        .on('mouseover', function () {
          store.triggerInteraction(interactions, selected);
        })
        .on('mouseout', function () {
          store.resetInteraction(interactions);
        });
    });
};

export type DataLabel = {
  alignmentbaseline?: string;
  backgroundColor?: string;
  color?: string;
  hidden?: boolean;
  isInactive?: boolean;
  // Expand the padding in place or offset
  offsetPadding?: boolean;
  selected: string[];
  selectedBackgroundColor?: string;
  selectedColor?: string;
  text: string;
  textAnchor?: string;
  x: number;
  y: number;
};

export const addDataLabels = (
  selection: Selection<SVGGElement, unknown, null, undefined>,
  data: DataLabel[],
  store: InteractionStore
) => {
  const getPadding = (d: DataLabel) => {
    if (d.text.length === 1) return 36;
    if (d.text.length === 2) return 24;
    if (d.text.length === 3) return 12;

    return 0;
  };

  selection
    .selectAll('.data-label')
    .data(data)
    .enter()
    .append('g')
    .append('text')
    .text(function (d) {
      return d.text;
    })
    .attr('x', function (d) {
      return d.x + (d?.offsetPadding ? getPadding(d) / 2 : 0);
    })
    .attr('y', function (d) {
      return d.y;
    })
    .attr('font-family', 'PPNeueMachina')
    .attr('font-weight', '700')
    .attr('font-size', 'calc(14px * var(--scaling-ratio, 1))')
    .attr('alignment-baseline', function (d) {
      return d?.alignmentbaseline || 'middle';
    })
    .attr('text-anchor', function (d) {
      return d?.textAnchor || 'middle';
    })
    .attr('fill', function (d) {
      return d?.color || '#000000';
    })
    .style('--color', function (d) {
      return d?.color || '#000000';
    })
    .style('--selectedColor', function (d) {
      return d?.selectedColor || '#000000';
    })
    .style('opacity', function (d) {
      return d.hidden ? 0 : 1;
    })
    .style('--opacity', function (d) {
      return d.hidden ? 0 : 1;
    })
    .each(function (d) {
      const selected = d?.selected || [];
      const classname = `${INTERACTION_CLASS} ${labelInteraction.classname} ${selected?.join(' ')}`;
      const backgroundClassname = `${INTERACTION_CLASS} ${labelBackgroundInteraction.classname} ${selected?.join(' ')}`;
      const interactions = [barInteraction, labelInteraction, labelBackgroundInteraction];

      d3.select(this)
        .style('cursor', 'pointer')
        .attr('class', classname)
        .on('mouseover', function () {
          if (!d.isInactive) store.triggerInteraction(interactions, selected);
        })
        .on('mouseout', function () {
          if (!d.isInactive) store.resetInteraction(interactions);
        });

      const { x, y, height, width } = this.getBBox();
      const paddingX = 16;
      const paddingY = 20;
      const extraPaddingX = getPadding(d);

      d3.select(this.parentElement)
        .insert('rect', ':first-child')
        .attr('x', x - paddingX / 2 - extraPaddingX / 2)
        .attr('y', y - paddingY / 2)
        .attr('width', width + paddingX + extraPaddingX)
        .attr('height', height + paddingY)
        .attr('rx', '20px')
        .attr('class', backgroundClassname)
        .attr('alignment-baseline', function () {
          return d?.alignmentbaseline || 'middle';
        })
        .attr('text-anchor', function () {
          return d?.textAnchor || 'middle';
        })
        .attr('fill', function () {
          return d?.backgroundColor || 'transparent';
        })
        .style('--backgroundColor', function () {
          return d?.backgroundColor || 'transparent';
        })
        .style('--selectedBackgroundColor', function () {
          return d?.selectedBackgroundColor || '#ffffff';
        })
        .style('cursor', 'pointer')
        .on('mouseover', function () {
          if (!d.isInactive) store.triggerInteraction(interactions, selected);
        })
        .on('mouseout', function () {
          if (!d.isInactive) store.resetInteraction(interactions);
        });
    });
};

export type ChartSizes = {
  chart: {
    width: number;
    height: number;
  };
  graph: {
    width: number;
    height: number;
  };
  margin: number;
  width: number;
  height: number;
};

type ChartOptions = {
  alt: string;
  colorPalette?: ColorPalette | undefined | null;
  width?: number;
  height?: number;
  margin?: number;
  xAxisGap?: number;
  yAxisGap?: number;
};

/**
 * This is the main function that each chart uses.
 * It creates a standardized selector and measurements for the chart area.
 */
export const setUpChart = (chartElement: HTMLElement, options?: ChartOptions) => {
  const {
    alt,
    colorPalette = getPalette(),
    // Defaults to a 3:2 aspect ratio
    width = 1080,
    height = 720,
    margin = 0,
    xAxisGap = 0,
    yAxisGap = 0
  } = options || {};

  const chartWidth = width - margin * 2;
  const chartHeight = height - margin * 2;
  const sizes: ChartSizes = {
    chart: {
      width: chartWidth,
      height: chartHeight
    },
    graph: {
      width: chartWidth - yAxisGap,
      height: chartHeight - xAxisGap
    },
    margin,
    width,
    height
  };

  const svg = d3
    .select(chartElement)
    .append('svg')
    .attr('width', '100%')
    .attr('height', '100%')
    .attr('viewBox', `0 0 ${width} ${height}`);

  // Must be the first child of the svg for the screen reader to recognize
  if (alt) {
    svg.append('title').text(alt);
  }

  // Define gradients
  if (colorPalette?.isGradient) addGradients(colorPalette.colors);

  // Draw a stage for the chart with margins
  const selection = svg.append('g').attr('transform', 'translate(' + margin + ',' + margin + ')');

  return { selection, sizes };
};

export const isChartLarge = (gridColspan: number) => {
  return gridColspan > 6;
};
